import React from "react";

import { noop, some } from "lodash/fp";
import { useLocation, useSearchParams } from "react-router-dom";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import {
  StepCardContent,
  StepDescription,
  StepHeader,
  StepTitle,
  StepFooter,
} from "@app/analytics/onboarding/steps/styles";
import type { OnboardingContext } from "@app/analytics/onboarding/types";
import useAuth from "@app/common/auth/useAuth";
import useApps from "@app/common/hooks/useApps";
import ROUTES from "@app/common/routes";
import { withQueryParamsUrl } from "@app/common/utils";
import { isRssInstalled } from "@app/utils/browserUtils";

import Button from "@rui/Button";
import { Column, Row } from "@rui/Stack";

import WrapUpUpcomingEvents from "./WrapUpUpcomingEvents";
import {
  WrapUpUpsellType,
  CustomerExperienceOptIn,
  CustomerExperienceOptInUpsell,
  RssExtensionUpsell,
  ZoomUpsell,
} from "./wrapUpUpsells";

const getWrapUpUpsellType = ({
  fromOrInMeet,
  isZoomConnected,
  userHasRssInstalled,
}: {
  fromOrInMeet: boolean;
  isZoomConnected: boolean;
  userHasRssInstalled: boolean;
}): WrapUpUpsellType => {
  if (fromOrInMeet) return "cx_opt_in";
  return isZoomConnected
    ? userHasRssInstalled
      ? "cx_opt_in"
      : "rss_extension"
    : "connect_zoom";
};

interface WrapUpStepDefaultContentProps {
  context: OnboardingContext;
  onFinish: (
    event: React.MouseEvent,
    options: { path?: string; newWindow?: boolean; confetti: boolean }
  ) => void;
}

const WrapUpStepDefaultContent = ({
  context,
  onFinish,
}: WrapUpStepDefaultContentProps) => {
  const location = useLocation();
  const [queryParams] = useSearchParams();

  const { client } = useApps();
  const { user } = useAuth();
  const { userSettings, updateUserSettings } = useUserSettings();

  const { connectedAccountProviders, addAccountProvider } =
    useConnectedProviders();
  const isZoomConnected = some(
    { providerId: "zoom" },
    connectedAccountProviders
  );

  const redirectTo = queryParams.get("redirectTo");
  const userHasRssInstalled = isRssInstalled();

  const handleToggleCustomerExperienceOptIn = (
    event: React.SyntheticEvent,
    checked: boolean
  ) => {
    updateUserSettings.mutate({ analyticsOptIn: checked }, { onSuccess: noop });
  };

  const handleConnectZoom = () => {
    addAccountProvider("zoom", {
      returnPath: location.pathname,
      errorReturnPath: location.pathname,
      queryParams,
    });
  };

  const handleFinish = (event: React.MouseEvent) => {
    if (context.source === "meet" && client !== "meet") {
      window.close();
    } else {
      // if they onboarded in notetaker, send them back to notetaker. otherwise,
      // send them to the landing page.
      const defaultUrl =
        client === "meet"
          ? withQueryParamsUrl(ROUTES.MEETING_TOOLS_HOME, {}, queryParams)
          : ROUTES.LANDING_PAGE;
      onFinish(event, { path: redirectTo ?? defaultUrl, confetti: true });
    }
  };

  const upsell = getWrapUpUpsellType({
    isZoomConnected,
    userHasRssInstalled,
    fromOrInMeet: client === "meet" || context.source === "meet",
  });

  // only show the CX opt-in in the footer if we're not already showing it as the primary upsell
  const showCxOptInInFooter = upsell !== "cx_opt_in";

  return (
    <>
      <StepCardContent spacing="40px">
        <StepHeader>
          <StepTitle>
            {context.source === "meet"
              ? `Welcome to Read Notetaker${
                  user?.firstName ? `, ${user.firstName}` : "!"
                }`
              : "Wrap Up"}
          </StepTitle>
          <StepDescription>
            You're almost done! Just a few more things.
          </StepDescription>
        </StepHeader>
        <Column spacing={2}>
          <WrapUpUpcomingEvents />
          {upsell === "connect_zoom" && (
            <ZoomUpsell onConnect={handleConnectZoom} />
          )}
          {upsell === "rss_extension" && <RssExtensionUpsell />}
          {upsell === "cx_opt_in" && (
            <CustomerExperienceOptInUpsell
              optedIn={Boolean(userSettings?.analyticsOptIn)}
              onChange={handleToggleCustomerExperienceOptIn}
            />
          )}
        </Column>
      </StepCardContent>
      <StepFooter sx={{ padding: 2 }}>
        <Column maxWidth="600px" spacing={2}>
          {showCxOptInInFooter && (
            <Row paddingX={2}>
              <CustomerExperienceOptIn
                optedIn={Boolean(userSettings?.analyticsOptIn)}
                onChange={handleToggleCustomerExperienceOptIn}
              />
            </Row>
          )}
          <Row variant="center">
            <Button onClick={handleFinish}>
              Finish
              {context.source === "meet"
                ? client === "meet"
                  ? " & return to Notetaker"
                  : " & return to Google Meet"
                : "! 🎉"}
            </Button>
          </Row>
        </Column>
      </StepFooter>
    </>
  );
};

export default WrapUpStepDefaultContent;
